import { useState, type ChangeEvent, type FormEvent } from "react";
import { cn } from "../lib/cn";
import { forgotPassword } from "../lib/api";
import styles from "./LoginForm.module.css";

interface LoginFormProps {
  /** Called with the entered credentials when the form is submitted. */
  onSubmit: (email: string, password: string) => void | Promise<void>;
  /** Error message to display, if any. */
  error?: string | null;
  /** Disables the submit button while a request is in flight. */
  submitting?: boolean;
}

/**
 * Presentational sign-in form. Owns only local field state; credential
 * submission is delegated to `onSubmit`.
 */
export function LoginForm({ onSubmit, error, submitting = false }: LoginFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotState, setForgotState] = useState<"idle" | "sending" | "sent">("idle");

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void onSubmit(email, password);
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setPassword(event.target.value);
  };

  // Non-leaking by contract: the server always succeeds, so the confirmation
  // never reveals whether the address has an account.
  const handleForgotSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (forgotState === "sending") return;
    setForgotState("sending");
    try {
      await forgotPassword(forgotEmail.trim());
      setForgotState("sent");
    } catch {
      setForgotState("idle");
      setForgotOpen(false);
    }
  };

  return (
    <form className={styles.root} onSubmit={handleSubmit} noValidate>
      <h1 className={styles.title}>Sign in to AFS Jobs</h1>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <label className={styles.field}>
        <span className={styles.label}>Email</span>
        <input
          className={styles.input}
          type="email"
          name="email"
          autoComplete="username"
          value={email}
          onChange={handleEmailChange}
          required
        />
      </label>
      <label className={styles.field}>
        <span className={styles.label}>Password</span>
        <input
          className={styles.input}
          type="password"
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={handlePasswordChange}
          required
        />
      </label>
      <button
        type="submit"
        className={cn(styles.submit, { [styles.disabled]: submitting })}
        disabled={submitting}
      >
        {submitting ? "Signing in…" : "Sign in"}
      </button>

      {forgotOpen ? (
        <form className={styles.forgot} onSubmit={(event) => void handleForgotSubmit(event)} noValidate>
          {forgotState === "sent" ? (
            <p className={styles.forgotNote}>
              If that email has an account, a reset link is on its way.
            </p>
          ) : (
            <>
              <label className={styles.field}>
                <span className={styles.label}>Account email</span>
                <input
                  className={styles.input}
                  type="email"
                  name="forgotEmail"
                  autoComplete="email"
                  value={forgotEmail}
                  onChange={(event) => setForgotEmail(event.currentTarget.value)}
                />
              </label>
              <button type="submit" className={cn(styles.submit, styles.forgotSubmit)} disabled={forgotState === "sending"}>
                {forgotState === "sending" ? "Sending…" : "Email reset link"}
              </button>
            </>
          )}
        </form>
      ) : (
        <button type="button" className={styles.forgotLink} onClick={() => setForgotOpen(true)}>
          Forgot password?
        </button>
      )}
    </form>
  );
}
